import { useContext, useEffect } from "react"
import { useParams } from "react-router-dom"
import { useAppointmentQuery } from "../../api/fetchAppointment"
import { useHealthcareServiceQuery } from "../../api/fetchHealthcareService"
import { ContactCard } from "../../components/careCard/ContactCard"
import { ContactDetails } from "../../components/contactDetails/ContactDetails"
import { Loading } from "../../components/loading/Loading"
import { AuthenticationContext } from "../../context/AuthenticationContext"
import { BackButtonContext } from "../../context/BackButtonContext"
import { AppointmentCard } from "../appointment/appointmentViews/AppointmentCard"
import { CancellationOptions } from "../appointment/appointmentViews/CancellationOptions"
import { AppointmentStatusMessage } from "./components/AppointmentStatusMessage"
import { useNhsAppJs } from "../../hooks/useNhsAppJs"
import { capitalise } from "../../utils/stringFormatters"
import { SimplifiedAppointment } from "../../appointment.types"
import { HealthcareService, Location } from "fhir/r3"
import { useLocationQuery } from "../../api/fetchLocation"
import { readonlyMode } from "../../constants/readonly"

export const AppointmentSummary = () => {
  const { appointmentId, odsId } = useParams()

  const { token } = useContext(AuthenticationContext)
  const appointmentQuery = useAppointmentQuery({
    appointmentId,
    token,
  })
  const appointment = appointmentQuery.data
  const locationId = appointment?.locationId

  const healthcareServiceQuery = useHealthcareServiceQuery({
    odsId: odsId,
    token,
  })
  const healthcareService = healthcareServiceQuery.data

  const { setOnBackPress, setShowBackButton } = useContext(BackButtonContext)
  const { inApp, navigateToPage } = useNhsAppJs()

  useEffect(() => {
    if (inApp && setOnBackPress && setShowBackButton) {
      setOnBackPress("appointments")
      setShowBackButton(true)
    } else if (setShowBackButton) {
      setShowBackButton(false)
    }
  }, [inApp, setOnBackPress, setShowBackButton])

  useEffect(() => {
    if (appointmentQuery?.data?.specialty) {
      document.title = capitalise(appointmentQuery?.data?.specialty.toString())
    }
  }, [appointmentQuery?.data?.specialty])

  const { data: location } = useLocationQuery({ locationId, token })

  if (healthcareServiceQuery.isLoading || appointmentQuery.isLoading) return <Loading />

  if (healthcareServiceQuery.isError || appointmentQuery.isError) {
    navigateToPage("error")
    return null
  }

  if (!healthcareService || !appointment) {
    navigateToPage("error")
    return null
  }

  return (
    <AppointmentSummaryContent
      appointment={appointment}
      appointmentId={appointmentId}
      healthcareService={healthcareService}
      location={location}
      readonly={readonlyMode(token)}
    />
  )
}

type AppointmentSummaryContentProps = {
  appointment: SimplifiedAppointment
  appointmentId?: string
  healthcareService: HealthcareService
  location?: Location
  readonly: boolean
}

export const AppointmentSummaryContent = ({
  appointment,
  healthcareService,
  appointmentId,
  location,
  readonly,
}: AppointmentSummaryContentProps) => {
  return (
    <>
      <h1 className="appointment-header">{appointment.specialty}</h1>
      {appointment.status !== "booked" && (
        <AppointmentStatusMessage
          healthcareService={healthcareService}
          appointmentId={appointmentId ?? ""}
          appointmentStatus={appointment.status}
        />
      )}

      <AppointmentCard
        appointment={appointment}
        healthcareService={healthcareService}
        appointmentView={false}
        location={location}
        canReschedule={!readonly}
      />

      {["booked", "fulfilled"].includes(appointment.status) && (
        <ContactCard
          contact="your care provider"
          bullets={["your condition has changed", "you think you have a different condition"]}
        />
      )}
      <ContactDetails healthcareService={healthcareService} header="Who to contact" location={location} />
      {!readonly && <CancellationOptions appointment={appointment} healthcareService={healthcareService} />}
    </>
  )
}
